import { Card, CardContent, CardHeader, CardTitle } from '@1trading/react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CursorPagination, INITIAL_PAGINATION, type PaginationState, registerNextPageCursor } from '@/components/data-table/cursor-pagination';
import { DataTable } from '@/components/data-table/data-table';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { type FilterState, TableFilters } from '@/components/filter-bar';
import { eventFilterConfigs, filtersToEventRequest } from '@/features/abuse/abuse-filters';
import { abuseSubjectPath } from '@/features/abuse/abuse-meta';
import { EVENT_COLUMNS } from '@/features/abuse/events/event-columns';
import { SubjectTypeToggles } from '@/features/abuse/subject-type-toggles';
import { useAbuseEvents } from '@/hooks/queries/compliance/use-abuse';
import { getErrorMessage } from '@/lib/api/error';
import type { AbuseSubjectType, AbuseTimeRange } from '@/services/api/abuse';

const EVENTS_PAGE_SIZE = 25;

/** Cursors are minted against one window, feed and filter set: key this card on all of them so any change restarts the walk */
interface EventsCardProps {
  range: AbuseTimeRange;
  services: string[];
  subjectType: AbuseSubjectType;
  filters: FilterState;
  onSubjectTypeChange: (subjectType: AbuseSubjectType) => void;
  onFiltersChange: (filters: FilterState) => void;
}

export const EventsCard = ({ range, services, subjectType, filters, onSubjectTypeChange, onFiltersChange }: EventsCardProps) => {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState<PaginationState>(INITIAL_PAGINATION);
  const request = useMemo(() => ({ ...range, ...filtersToEventRequest(filters), subjectType, limit: EVENTS_PAGE_SIZE }), [filters, range, subjectType]);
  const filterConfigs = useMemo(() => eventFilterConfigs(services), [services]);
  const cursor = pagination.cursors[pagination.currentPage];
  const query = useAbuseEvents({ ...request, ...(cursor ? { cursor } : {}) });

  useEffect(() => {
    const nextCursor = query.data?.cursor;

    if (!nextCursor) return;

    setPagination((previous) => registerNextPageCursor(previous, nextCursor));
  }, [query.data]);

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle>Recent events</CardTitle>
        <SubjectTypeToggles label="Event feed" onChange={onSubjectTypeChange} value={subjectType} />
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TableFilters configs={filterConfigs} onChange={onFiltersChange} rows={[]} state={filters} />
          <CursorPagination
            ariaLabel="Event pages"
            currentPage={pagination.currentPage}
            onPageChange={(page) => setPagination((previous) => ({ ...previous, currentPage: page }))}
            totalPages={pagination.totalPages}
          />
        </div>
        {query.isError ? (
          <QueryErrorBanner message={`Failed to fetch events: ${getErrorMessage(query.error)}`} onRetry={() => query.refetch()} />
        ) : (
          <DataTable
            columns={EVENT_COLUMNS}
            data={query.data?.events}
            emptyMessage="No events match the current filters"
            getRowId={(row, index) => `${row.occurredAt}-${row.actorId}-${index}`}
            isLoading={query.isFetching}
            onRowClick={(row) => navigate(abuseSubjectPath({ subjectType: row.actorType, subjectId: row.actorId }))}
            surface={false}
          />
        )}
      </CardContent>
    </Card>
  );
};
EventsCard.displayName = 'EventsCard';
